import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as popups from '../../../../constants/popup-types';
import * as routes from '../../../../constants/routes';
import { addPopup, resetLegalAdviceQuestionSubmit, submitLegalAdviceQuestion } from '../../../../redux/actions';
import Validate from '../../../../app/validation/validation';
import TopBanner from '../../sections/top_banner';
import Select from '../../../../components/forms/select';
import './index.scss';
import { useSubmitLegalAdviceQuestionMutation } from 'redux/api/relations.api';

const NewRoute = ({legalAdvicePage}) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const translations = useSelector((store) => store.config.Translations);
    const userData = useSelector((store) => store.config.UserData);
    const [submitLegalAdviceQuestion] = useSubmitLegalAdviceQuestionMutation();
    const [legalAdviceQuestionSubmit, setLegalAdviceQuestionSubmit] = useState(false);  
    const [formState, setFormState] = useState({
        category_id: {
            value: '',
            isValid: false,
            rules: ['only_numbers', 'not_empty'],
            errorMessage: '',
        },
        email: {
            value: userData.email,
            isValid: false,
            rules: ['email', 'not_empty'],
            errorMessage: '',
        },
        telephone: {
            value: userData.phone,
            isValid: false,
            rules: ['phone', 'not_empty'],
            errorMessage: '',
        },
        content: {
            value: '',
            isValid: false,
            rules: ['not_empty'],
            errorMessage: '',
        },
    });

    const [isFormLoading, setIsFormLoading] = useState(false);

    const selectOptions = Object.values(legalAdvicePage.categories).map(
        (categoryData) => ({ id: categoryData.id, text: categoryData.title })
    );

    const validateField = (name, value) => {
        const validation = Validate(value, formState[name].rules);

        const isValid = validation.valid;
        const errorMessage = validation.msg;

        return { ...formState[name], value, isValid, errorMessage };
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();

        const newFormState = formState;
        const formFieldsList = Object.keys(newFormState);

        formFieldsList.forEach((fieldKey) => {
            newFormState[fieldKey] = validateField(fieldKey, newFormState[fieldKey].value);
        });

        setFormState({ ...newFormState });

        if (formFieldsList.every((fieldKey) => newFormState[fieldKey].isValid)) {
            const response = await submitLegalAdviceQuestion({
                category_id: newFormState.category_id.value,
                email: newFormState.email.value,
                telephone: newFormState.telephone.value,
                content: newFormState.content.value,
            });

            setLegalAdviceQuestionSubmit(response.data);
            setIsFormLoading(true);
        }
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;

        setFormState({
            ...formState,
            [name]: validateField(name, value),
        });
    };

    const handleSelectChange = (selectedValue) => setFormState({
        ...formState,
        category_id: validateField('category_id', selectedValue),
    });

    if (!userData) {
        history.push(routes.legal_advice);
        dispatch(addPopup({
            type: popups.LOGIN,
            subtitleText: translations['legal_advice_login_required'],
        }));
    }

    // On submission success, display a dedicated popup
    useEffect(() => {
        if (legalAdviceQuestionSubmit !== false) {
            dispatch(resetLegalAdviceQuestionSubmit());

            dispatch(addPopup({
                type: popups.GENERAL_MESSAGE,
                text: translations['legal_advice_question_submitted_message'],
                btnText: translations['legal_advice_question_submitted_button'],
                action: () => history.push(routes.legal_advice),
            }));

            setIsFormLoading(false);
        }
    }, [legalAdviceQuestionSubmit]);

    return (
        <>
            <TopBanner
                legalAdvicePage={legalAdvicePage}
                additionalBreadcrumb={{
                    text: 'פנייה',
                    route: routes.legal_advice_routes.new_question,
                }}
            />

            <div className="legal-advice-new-route content-container">
                <h4 className="new-question-title">
                    {translations['legal_advice_new_question_title']}
                </h4>

                <form className="new-question-form" onSubmit={handleFormSubmit}>
                    <div className="new-question-flex-row">
                        <label className="new-question-label">
                            {translations['legal_advice_new_question_name']}

                            <input
                                type="text"
                                className="new-question-input"
                                value={`${userData.first_name} ${userData.last_name}`}
                                readOnly
                            />
                        </label>

                        <label className="new-question-label">
                            {translations['legal_advice_new_question_id']}

                            <input
                                type="text"
                                className="new-question-input"
                                value={userData.user_id}
                                readOnly
                            />
                        </label>
                    </div>

                    <div className="new-question-flex-row">
                        <label className="new-question-label">
                            {translations['legal_advice_new_question_email']}

                            <input
                                name="email"
                                type="email"
                                className="new-question-input"
                                value={formState.email.value}
                                onChange={handleInputChange}
                            />

                            {formState.email.errorMessage && (
                                <p className="new-question-error">
                                    {formState.email.errorMessage}
                                </p>
                            )}
                        </label>

                        <label className="new-question-label">
                            {translations['legal_advice_new_question_telephone']}

                            <input
                                name="telephone"
                                type="tel"
                                className="new-question-input"
                                value={formState.telephone.value}
                                onChange={handleInputChange}
                            />

                            {formState.telephone.errorMessage && (
                                <p className="new-question-error">
                                    {formState.telephone.errorMessage}
                                </p>
                            )}
                        </label>
                    </div>

                    <label className="new-question-label">
                        {translations['legal_advice_new_question_category']}

                        <Select
                            name="category_id"
                            className="new-question-select"
                            placeholder={translations['legal_advice_new_question_category_placeholder']}
                            options={selectOptions}
                            value={formState.category_id.value}
                            onChange={handleSelectChange}
                        />

                        {formState.category_id.errorMessage && (
                            <p className="new-question-error">
                                {formState.category_id.errorMessage}
                            </p>
                        )}
                    </label>

                    <label className="new-question-label">
                        {translations['legal_advice_new_question_content']}

                        <textarea
                            name="content"
                            className="new-question-input new-question-input_type_textarea"
                            value={formState.content.value}
                            onChange={handleInputChange}
                        />

                        {formState.content.errorMessage && (
                            <p className="new-question-error">
                                {formState.content.errorMessage}
                            </p>
                        )}
                    </label>

                    <div className="new-question-button-container">
                        <button
                            type="submit"
                            className="new-question-button"
                            disabled={isFormLoading}
                        >
                            {
                                isFormLoading
                                    ? translations['legal_advice_new_question_submitting']
                                    : translations['legal_advice_new_question_submit']
                            }
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default NewRoute;
